import angular from 'angular';
import booksDom from '../../booksDom';
import booksService from '../../booksServices';
import returnItemsTemplate from './returnItems.template.html';
import './returnItems.scss';
/** Return Services */
import { ReturnsServiceProvider } from './services/ReturnsService';
/** Check */
import { ReturnItemsRunCheck } from './services/Data/Check';
/** Payment Item */
import { ReturnItemsRunPaymentItem } from './services/Data/PaymentItem';
/** Receipt */
import { ReturnItemsRunReceipt } from './services/Data/Receipt';
/** Return Item Grid Item */
import { ReturnItemRunGridItem } from './services/Data/ReturnItemGridItem';
/** Return Receipt */
import { ReturnItemRunReturnReceipt } from './services/Data/ReturnReceipt';
/** Barcode Search */
import { barcodeSearchComponent } from './dom/barcodeSearch';
/** Refund Payments */
import { refundPaymentsComponent } from './dom/refundPayments';
/** Refund Totals */
import { refundTotalsComponent } from './dom/refundTotals';
/** Receipt Payments Dialog */
import receiptPaymentsDialog from './dom/receiptPaymentsDialog';
import cutoffPaymentsDialog from './dom/cutoffPaymentsDialog';

/*******************************************************************************
 * @ngdoc  component
 * @module csm.books.com.returnItems
 * @name   csm.books.com.returnItems.component:Component
 * @description
 * The Return Receipts application is used to return items from a receipt back to
 * inventory (if inventory tracking is enabled). This will remove the sale from
 * any reports as long as the date range of the report includes both the original
 * sale and the return, similar to a void. Because this doesn't change the
 * original receipt, the original receipt will still be deposited as normal.
 * However, the original receipt can no longer be voided.
 *******************************************************************************/
export default angular
  .module('csm.books.com.returnItems', [
    booksDom,
    booksService,
    'ui.grid',
    'ui.grid.edit',
    'ui.grid.cellNav',
    'CSMCommonWeb',
    'ngAnimate',
    receiptPaymentsDialog,
    cutoffPaymentsDialog
  ])
  .component('returnItems', {
    controller: Controller,
    template: returnItemsTemplate,
    bindings: {
      returnRoute: '@',
      ledgerItemIds: '<'
    }
  })
  /** Return Services */
  .provider('ReturnsService', ReturnsServiceProvider)
  /** Data */
  .factory('Data', initDataFactory)
  /** Barcode Search */
  .component('barcodeSearch', barcodeSearchComponent)
  /** Refund Payments */
  .component('refundPayments', refundPaymentsComponent)
  /** Refund Totals */
  .component('refundTotals', refundTotalsComponent)
  /** Check */
  .run(ReturnItemsRunCheck)
  /** Payment Item */
  .run(ReturnItemsRunPaymentItem)
  /** Receipt */
  .run(ReturnItemsRunReceipt)
  /** Return Item Grid Item */
  .run(ReturnItemRunGridItem)
  /** Return Receipt */
  .run(ReturnItemRunReturnReceipt).name;

initDataFactory.$inject = [];

/*******************************************************************************
 * initDataFactory */
/**
 * Returns the initial (empty) data object.
 *******************************************************************************/
/* @ngInject */ function initDataFactory() {
  return {};
}

/*******************************************************************************
 * Controller */
/**
 * Controls the work flow for the component.
 *******************************************************************************/
/* @ngInject */ function Controller($injector, $scope) {
  var $svcs = { $ctrl: this, $global: {}, $scope };

  $injector.invoke(initCtrl, null, Object.assign($svcs, { $svcs }));
  $injector.invoke(initGlobal, null, $svcs);
}

/*******************************************************************************
 * initCtrl */
/**
 * Initializes the controller properties and functions.
 *******************************************************************************/
/* @ngInject */ function initCtrl(
  $ctrl,
  $injector,
  $svcs,
  LocalDateSerializer,
  SERVER,
  Tools
) {
  Object.assign($ctrl, {
    /*------------*/
    /* Properties */
    /*------------*/
    amounts: {
      cash: 0,
      check: 0,
      funds: 0,
      journal: 0
    },
    cardsToReturn: [],
    checksToReturn: [],
    receivableCharge: null,
    /** Whether this receipt currently has any active payments made
        elsewhere against its own items (see ReceiptPaymentsDialog) - drives
        the "Receipt Payments" button under the checks/cards grid, which
        only shows once this is known to be true. */
    hasReceiptPayments: false,
    /** The active payments made elsewhere that the loaded item balances
        reflect - the baseline showReceiptPayments compares against. */
    receiptPayments: [],
    reductionToReceipt: 0,
    returnDate: LocalDateSerializer.deserialize(
      SERVER.BooksWebConstants.defaultReturnDate
    ),
    uiFlags: {},
    validators: {},

    /*-----------*/
    /* Functions */
    /*-----------*/
    $onInit: $injector.invoke($onInit, null, $svcs),
    buildCheck: $injector.invoke(buildCheck, null, $svcs),
    buildItemGrid: $injector.invoke(buildItemGrid, null, $svcs),
    getAccounts: $injector.invoke(getAccounts, null, $svcs),
    getLists: $injector.invoke(getLists, null, $svcs),
    getReceipt: $injector.invoke(getReceipt, null, $svcs),
    returnComplete: $injector.invoke(returnComplete, null, $svcs),
    savePreCheck: $injector.invoke(savePreCheck, null, $svcs),
    saveReturn: $injector.invoke(saveReturn, null, $svcs),
    selectItem: $injector.invoke(selectItem, null, $svcs),
    showReceiptPayments: $injector.invoke(showReceiptPayments, null, $svcs)
  });

  /*-----------------------------------------------------------------------*/
  /* Utility function for the refund and balaneReduction properties. This  */
  /* expects to be passed in as the reduce function for an array of items. */
  /*-----------------------------------------------------------------------*/
  function propertySum(prop) {
    return function (total, item) {
      if (!prop || !item || item[prop] === undefined || !item.hasReturn)
        return total;

      return total + item[prop];
    };
  }

  /*--------------------------*/
  /* Create amount accessors. */
  /*--------------------------*/
  Object.defineProperties($ctrl.amounts, {
    refund: {
      get: () =>
        Tools.roundMoney(
          $ctrl.itemsGrid.data.reduce(propertySum('returnAmount'), 0)
        )
    },

    /*-------------------------------------------------------------------*/
    /* Per row, not pooled: the server books each item against its own   */
    /* balance (ItemReturn.bookResults), so one item's unpaid balance    */
    /* can't absorb another item's refund. Pooling them asked for too    */
    /* little cash back on a mixed return (e.g. a paid $50 item with two */
    /* $10 shirts still owed) and the save failed with "journal debits   */
    /* do not match credits".                                            */
    /*-------------------------------------------------------------------*/
    balanceReduction: {
      get: () =>
        Tools.roundMoney(
          $ctrl.itemsGrid.data.reduce(
            (total, item) =>
              item && item.hasReturn
                ? total +
                  Math.max(
                    Math.min(item.returnAmount || 0, item.balanceDue || 0),
                    0
                  )
                : total,
            0
          )
        )
    },

    totalDue: {
      get: () => {
        const refund = $ctrl.amounts.refund;
        const balanceReduction = $ctrl.amounts.balanceReduction;
        const paymentsReturned =
          $ctrl.amounts.checkReturn + $ctrl.amounts.cardReturn;

        return Tools.roundMoney(refund - (balanceReduction + paymentsReturned));
      }
    }
  });

  /*--------------*/
  /* UI Accessors */
  /*--------------*/
  Object.defineProperties($ctrl.uiFlags, {
    hasClearingAccount: {
      get: () => $ctrl.clearingAccountID
    },
    hasFundsAccount: {
      get: () => $ctrl.studentFundsAccountID
    },
    hasFundsReturn: {
      get: () =>
        $ctrl.itemsGrid.data.some(
          (item) => item.isStudentFunds && item.returnAmount > 0
        )
    }
  });
}

/*******************************************************************************
 * initGlobal */
/**
 * Initializes the global properties and functions.
 *******************************************************************************/
/* @ngInject */ function initGlobal($global, $scope, Watcher) {
  Object.assign($global, {
    /*------------*/
    /* Properties */
    /*------------*/
    Watch: new Watcher($scope)
  });
}

/*******************************************************************************
 * $onInit */
/**
 * This is called by angular when the DOM is ready.
 *******************************************************************************/
/* @ngInject */ function $onInit($ctrl, $q, BusyScreen) {
  return function () {
    /*-------------------*/
    /* Application Start */
    /*-------------------*/
    $q.all([
      $ctrl.buildItemGrid(),
      $ctrl.getAccounts(),
      $ctrl.getLists(),
      $ctrl.getReceipt()
    ]).then(function () {
      BusyScreen.setBusy(false);
    });
  };
}

/*******************************************************************************
 * buildCheck */
/**
 * If no check is needed, immediately resolves the promise. Otherwise, builds the
 * check object from the Write Check fields and adds a debit to the return for
 * the account that was set as the Clearing Account. If no Clearing Account is
 * found, rejects the promise and prevents the return from saving. The reject
 * strings hold a | so the handler can tell the alert body from the title.
 *
 * @param  payments  Payments list the check payment is added to.
 *******************************************************************************/
/* @ngInject */ function buildCheck(
  $ctrl,
  $q,
  BusyScreen,
  Data,
  ReturnsService,
  SERVER
) {
  return function (payments) {
    /*------------------------------------------*/
    /* If there is no check amount to write, we */
    /* have no need to create a check object.   */
    /*------------------------------------------*/
    if (!$ctrl.amounts.check) return $q.resolve();

    /*--------------------------------------------------------*/
    /* Check if the client has the On Account credit account. */
    /*--------------------------------------------------------*/
    if (!$ctrl.clearingAccountID)
      return $q.reject(
        'You need to set your "Clearing Account" in Account Admin to return any funds with a check.|Account Not Set'
      );

    /***
     * Double check that the entity is filled out before proceeding.
     */
    if (!$ctrl.checkEntity) {
      return $q.reject(
        'You must have an individual filled out for who we are writing the check to.| Check Entity Missing'
      );
    }

    /*-------------------------------------------------------------*/
    /* Add an account credit item to the return receipt item list. */
    /*-------------------------------------------------------------*/
    payments.push({
      type: SERVER.BooksDBConstants.LIT_JOURNAL_CREDIT,
      memo: 'Balance Owed to Customer',
      amount: $ctrl.amounts.check,
      specialAccountID: SERVER.BooksDBConstants.SAID_CLEARING,
      entityID: $ctrl.checkEntity.id
    });

    /*-------------------*/
    /* Create the check. */
    /*-------------------*/
    var c = Data.Check(new Date());
    c.amount = $ctrl.amounts.check;
    c.entityID = $ctrl.checkEntity.id;
    c.paidTo = $ctrl.checkEntity.name;
    c.handwritten = $ctrl.checkHandwritten;
    c.bankAccountID = $ctrl.checkBankID;

    /*-------------------------------------------------------*/
    /* Set the check number only if for a handwritten check. */
    /*-------------------------------------------------------*/
    if (c.handwritten) c.checkNumber = $ctrl.checkNumber;

    /*----------------------------*/
    /* Build the journal credits. */
    /*----------------------------*/
    c.creditItem = {
      type: SERVER.BooksDBConstants.LIT_JOURNAL_CREDIT,
      amount: c.amount,
      accountID: c.bankAccountID
    };

    /*---------------------------*/
    /* Build the journal debits. */
    /*---------------------------*/
    c.debitItem = {
      type: SERVER.BooksDBConstants.LIT_JOURNAL_DEBIT,
      amount: c.amount,
      specialAccountID: SERVER.BooksDBConstants.SAID_CLEARING,
      entityID: c.entityID
    };

    /*----------------------------------------------*/
    /* Load the entity and build the check address. */
    /*----------------------------------------------*/
    BusyScreen.setBusy(true);
    return ReturnsService.getEntityInfo(c.entityID).then(function (resp) {
      var ei = resp;

      /*--------------------*/
      /* Build the address. */
      /*--------------------*/
      var address = '';
      if (ei.addressInfo) {
        address = !ei.addressInfo.address
          ? ''
          : /*------------------------------------------------------*/
            /* Begin address with a carriage return after the name. */
            /*------------------------------------------------------*/
            '\n' +
            /*----------------------------------------------*/
            /* Replace carriage returns in the address with */
            /* newline so the address displays correctly    */
            /*----------------------------------------------*/
            (!ei.addressInfo.address
              ? ''
              : ei.addressInfo.address.trim().split(/\r/gm).join('\n') + '\n') +
            (!ei.addressInfo.city ? '' : ei.addressInfo.city + ', ') +
            (!ei.addressInfo.state ? '' : ei.addressInfo.state + ' ') +
            (!ei.addressInfo.zip ? '' : ei.addressInfo.zip + '\n');
      }

      c.address = c.paidTo + address;
      return c;
    });
  };
}

/*******************************************************************************
 * buildItemGrid */
/**
 * Builds the grid options. This will return a resolved promise, so it can be
 * used as part of a promise chain.
 *******************************************************************************/
/* @ngInject */ function buildItemGrid(
  $ctrl,
  $q,
  CommonUIGridTemplates,
  SERVER,
  uiGridConstants
) {
  /*----------------------------------------------*/
  /* Template for the product Description column. */
  /*----------------------------------------------*/
  var prodCellTemplate = `
  <div style="display:block; height: 100%; z-index:1;" tabindex="0">
    <div class="productMemoCell">
      <span class="ui-grid-cell-contents" ng-class="{'hasOptions': row.entity.hasOptions}"
            uib-tooltip-html="row.entity.tooltipHtml" tooltip-class="receiptOptionsTooltip" tooltip-append-to-body="true">
        {{COL_FIELD}}
      </span>
      <span ng-if="row.entity.alreadyReversedAmount"
            class="glyphicon glyphicon-warning-sign text-warning"
            style="margin-left: 4px;"
            uib-tooltip="{{row.entity.alreadyReversedAmount | currency}} of this item's value has already been reversed elsewhere (e.g. an NSF check) - returning it will account for what's actually still owed."
            tooltip-append-to-body="true">
      </span>
    </div>
  </div>`;

  return function () {
    $ctrl.itemsGrid = {
      enableCellEdit: false,
      enableCellEditOnFocus: true,
      enableColumnMenus: false,
      enableFiltering: false,
      enableRowSelection: false,
      enableFullRowSelection: false,
      enableRowHeaderSelection: false,
      multiSelect: false,
      enableSelectAll: false,
      saveFilter: false,
      saveSort: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      onRegisterApi: function (gridApi) {
        $ctrl.itemsGrid.api = gridApi;
      },
      minRowsToShow: 6,
      data: [],
      columnDefs: [
        {
          displayName: 'Item',
          field: 'itemName',
          minWidth: 100,
          cellTemplate: prodCellTemplate
        },
        {
          field: 'price',
          width: 80,
          cellFilter: 'currency',
          cellClass: 'text-right'
        },
        {
          displayName: 'Qty',
          field: 'quantity',
          width: 80,
          cellClass: 'text-right'
        },
        {
          field: 'tax',
          width: 80,
          cellFilter: 'currency',
          cellClass: 'text-right'
        },
        {
          displayName: 'Total',
          field: 'amount',
          width: 80,
          cellFilter: 'currency',
          cellClass: 'text-right'
        },
        {
          displayName: 'Return Qty',
          field: 'returnQuantity',
          type: 'number',
          cellEditableCondition: (scope) => !scope.row.entity.isAmountItem,
          enableCellEdit: true,
          editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
          precision: 0,
          width: 120,
          cellClass: 'text-right'
        },
        {
          displayName: 'Refund',
          field: 'returnAmount',
          cellEditableCondition: (scope) => scope.row.entity.isAmountItem,
          enableCellEdit: true,
          editableCellTemplate: CommonUIGridTemplates.CELL_NUMBER_INPUT,
          precision: 2,
          allowNegative: false,
          blankIsNan: true,
          cellFilter: 'currency',
          width: 100,
          cellClass: 'text-right'
        }
      ]
    };

    return $q.resolve();
  };
}

/*******************************************************************************
 * getAccounts */
/**
 * Gets the special accounts from the server.
 *******************************************************************************/
/* @ngInject */ function getAccounts($ctrl, ReturnsService) {
  return function () {
    return ReturnsService.getAccounts().then(function (resp) {
      $ctrl.mUFAccountID = resp.ufAccountID;
      $ctrl.clearingAccountID = resp.clearingAcctID;
      $ctrl.studentFundsAccountID = resp.studentFundsAcctID;
    });
  };
}

/*******************************************************************************
 * getLists */
/**
 * Gets the dataproviders from the server
 *******************************************************************************/
/* @ngInject */ function getLists($ctrl, BooksSettings, ReturnsService) {
  return function () {
    return ReturnsService.getLists(BooksSettings.ledgerEntityTypes()).then(
      function (resp) {
        $ctrl.accounts = resp.bankAccounts;
        $ctrl.entities = resp.entities;
        $ctrl.itemList = resp.items.sort(function (a, b) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });
      }
    );
  };
}

/*******************************************************************************
 * getReceipt */
/**
 * Loads the receipt for the id provided by the route.
 *******************************************************************************/
/* @ngInject */ function getReceipt(
  $ctrl,
  $global,
  $q,
  $routeParams,
  Alert,
  ReturnsService,
  BusyScreen,
  Data,
  SERVER,
  Tools,
  ReceiptPaymentsDialog,
  CutoffPaymentsDialog
) {
  return function () {
    /*-------------------------------------*/
    /* Deregister the watcher while we     */
    /* manipulate the receipt information. */
    /*-------------------------------------*/
    $global.Watch.deregister();

    BusyScreen.setBusy(true);

    return ReturnsService.getReceipt(Number($routeParams.id))
      .then(function (data) {
        /*--------------------------------------------------------*/
        /* Payments made before the 11.0.0 release can't be      */
        /* refunded (see the server's Receivables.               */
        /* getPaymentsBeforeRefundCutoff), so they're not offered */
        /* - asked only when the receipt has payments. (The NSF   */
        /* Checks screen only ever returns item sales, so it's    */
        /* never affected.)                                       */
        /*--------------------------------------------------------*/
        var hasPayments = new Data.Receipt(data).flatItemList.some(
          function (li) {
            return (
              li.type === SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT &&
              (li.subType === SERVER.BooksDBConstants.LIS_ITEM_PAYMENT ||
                li.subType === SERVER.BooksDBConstants.LIS_FINE_PAYMENT)
            );
          }
        );

        return $q.all([
          data,
          hasPayments
            ? ReturnsService.getPaymentsBeforeRefundCutoff(
                Number($routeParams.id)
              )
            : []
        ]);
      })
      .then(function (results) {
        var data = results[0];
        var cutoffPayments = results[1];
        var blockedPayments = [];

        /*----------------------------------------------------------------*/
        /* The ledgerItemIds is only used for the NSF Checks application. */
        /*----------------------------------------------------------------*/
        var ledgerItemIDs = ($ctrl.ledgerItemIds || []).map(function (li) {
          return li.ledgerItemID;
        });

        /*----------------------------------------------------*/
        /* This also runs as a reload, and the loops below    */
        /* only ever set these - clear them first so a charge */
        /* or processor from the previous load can't linger.  */
        /*----------------------------------------------------*/
        $ctrl.receivableCharge = null;
        $ctrl.hasProcessor = false;

        $ctrl.receipt = new Data.Receipt(data);
        $ctrl.itemsGrid.data = $ctrl.receipt.flatItemList
          .filter(function (li) {
            /*---------------------------------------------*/
            /* Get the reference for the charge item which */
            /* is indication of receipt put on account.    */
            /*---------------------------------------------*/
            if (
              li.subType === SERVER.BooksDBConstants.LIS_RECEIVABLE_ITEM_CHARGE
            )
              $ctrl.receivableCharge = li;

            /*-----------------------------------------------------*/
            /* The Convenience Fee Student Funds, and Donation are */
            /* special items that are represented as amounts only. */
            /*-----------------------------------------------------*/
            var isStudentFundsItem =
              li.type === SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_PURCHASE;
            var isConvenienceFee =
              li.type === SERVER.BooksDBConstants.LIT_SERVICE_FEE_CREDIT;
            /*---------------------------------------------------*/
            /* GR-2375: remainingReversibleAmount (server-        */
            /* computed, nets out every prior reversal across     */
            /* however many separate return receipts) replaces    */
            /* the raw Amount check for all four amount-only      */
            /* types (fee, student funds purchase, donation,      */
            /* payment), so a fully-reversed one drops out of the */
            /* grid instead of showing up again.                  */
            /*---------------------------------------------------*/
            var hasAmount = li.remainingReversibleAmount > 0;

            var isDonation =
              li.type === SERVER.BooksDBConstants.LIT_DONATION_PURCHASE;

            var isItemSale = li.type === SERVER.BooksDBConstants.LIT_ITEM_SALE;
            var isItemOrder =
              li.type === SERVER.BooksDBConstants.LIT_ITEM_ORDER;
            var hasOrders = li.qtyOrdered > 0;

            var isItemGroupSale =
              li.subType === SERVER.BooksDBConstants.LIS_ITEM_GROUP;
            /*---------------------------------------------------*/
            /* An internal receivable payment (cash/check paid   */
            /* directly against a charge or fine) is returnable  */
            /* the same way as a student funds purchase or       */
            /* convenience fee - a flat amount, no quantity.     */
            /* Destiny fine payments carry a distinct subType    */
            /* (LIS_DESTINY_FINE_PAYMENT) and are excluded here. */
            /*---------------------------------------------------*/
            var isReceivablePayment =
              li.type === SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT &&
              (li.subType === SERVER.BooksDBConstants.LIS_ITEM_PAYMENT ||
                li.subType === SERVER.BooksDBConstants.LIS_FINE_PAYMENT);

            var cutoffPayment =
              isReceivablePayment &&
              hasAmount &&
              cutoffPayments.find(function (p) {
                return p.paymentID === li.id;
              });

            if (cutoffPayment) {
              blockedPayments.push(cutoffPayment);
              return false;
            }

            var isAmountReturnable =
              (isStudentFundsItem ||
                isConvenienceFee ||
                isDonation ||
                isReceivablePayment) &&
              hasAmount;

            var isItemReturnable = (isItemSale || isItemOrder) && hasOrders;

            var isEmptyList = Tools.isEmpty(ledgerItemIDs);
            var itemIDFound = ledgerItemIDs.indexOf(li.id) > -1;

            /*---------------------------------------------*/
            /* If there is a convenience fee, update the   */
            /* memo as there is no memo for this normally. */
            /*---------------------------------------------*/
            if (isConvenienceFee && li.amount > 0) {
              li.memo = 'Convenience Fee';
            }

            return (
              !isItemGroupSale &&
              (isItemReturnable || isAmountReturnable) &&
              (isEmptyList || itemIDFound)
            );
          })
          .map(function (item) {
            return Data.ReturnItemGridItem(item);
          });

        /*---------------------------------------------------*/
        /* If there are no items available, we cannot return */
        /* anything and will return to the previous route.   */
        /* This now also covers a receipt made up solely of  */
        /* a receivable payment (no items sold) - that row   */
        /* flows into itemsGrid.data the same as any other   */
        /* returnable amount item, so it isn't empty here.   */
        /*---------------------------------------------------*/
        if (Tools.isEmpty($ctrl.itemsGrid.data)) {
          BusyScreen.setBusy(false);

          var showNoItems = function () {
            return Alert.show(
              'The chosen receipt has no items to return.'
            ).then(function () {
              $ctrl.returnComplete();
            });
          };

          if (blockedPayments.length)
            CutoffPaymentsDialog.show(blockedPayments).then(showNoItems);
          else showNoItems();
          return;
        }

        /*-------------------------------------------------------*/
        /* Reduce the balance due from the amount due on the     */
        /* items for non-legacy receipts or from the balance due */
        /* on the receipt. For non-legacy receipt items, create  */
        /* ReceivableItemCreditMemos for the balance to reduce.  */
        /*-------------------------------------------------------*/
        $ctrl.reductionToReceipt = 0;
        var reductionToItems = $ctrl.itemsGrid.data
          .filter(function (item) {
            return item.hasReturn;
          })
          .reduce(function (reduction, item) {
            return reduction + item.creditAmount;
          }, 0);

        if ($ctrl.receivableCharge)
          $ctrl.reductionToReceipt = Math.min(
            Tools.roundMoney(
              $ctrl.receivableCharge.amount - $ctrl.receipt.creditAmount
            ),
            Tools.roundMoney(
              Math.abs($ctrl.amounts.totalDue) - reductionToItems
            )
          );

        /*-------------------------------------------------------------*/
        /* Check for non-deposited, non-returned check received items. */
        /*-------------------------------------------------------------*/
        $ctrl.checks = $ctrl.receipt.paymentItems
          .filter(function (payment) {
            return (
              payment.subType === SERVER.BooksDBConstants.LIS_CHECK_RECEIVED &&
              !payment.referenceID &&
              !payment.isReturned
            );
          })
          .map(function (payment) {
            return new Data.PaymentItem(payment);
          });

        $ctrl.cards = $ctrl.receipt.paymentItems
          .filter(function (payment) {
            if (payment.processorType > SERVER.BooksDBConstants.PT_MANUAL)
              $ctrl.hasProcessor = true;

            return (
              payment.subType === SERVER.BooksDBConstants.LIS_CREDIT_CARD ||
              payment.subType === SERVER.BooksDBConstants.LIS_DEBIT_CARD ||
              payment.subType === SERVER.BooksDBConstants.LIS_WEBSTORE
            );
          })
          .map(function (payment) {
            return new Data.PaymentItem(payment);
          });

        /*-----------------------*/
        /* Start watching again. */
        /*-----------------------*/
        $global.Watch.register($ctrl.amounts);
        BusyScreen.setBusy(false);

        if (blockedPayments.length) CutoffPaymentsDialog.show(blockedPayments);

        /*--------------------------------------------------------------*/
        /* Receipt payment-elsewhere check: ask the server whether any   */
        /* of this receipt's items still have an active payment made     */
        /* against them on a SEPARATE Payment Receipt (see                */
        /* getReceiptPayments()'s doc comment) - that payment won't show */
        /* up anywhere on THIS screen. If so, show the "Receipt          */
        /* Payments" button under the checks/cards grid; the dialog only */
        /* opens when the user clicks it.                                */
        /*                                                                */
        /* This is deliberately NOT part of the promise chain above -    */
        /* it's its own extra request and shouldn't hold up the screen   */
        /* becoming usable.                                              */
        /*                                                                */
        /* The list itself is kept as the baseline the item balances     */
        /* above were loaded against - see showReceiptPayments.          */
        /*--------------------------------------------------------------*/
        ReceiptPaymentsDialog.getPayments(Number($routeParams.id)).then(
          function (payments) {
            $ctrl.receiptPayments = payments;
            $ctrl.hasReceiptPayments = payments.length > 0;
          }
        );
      });
  };
}

/*******************************************************************************
 * showReceiptPayments */
/**
 * Opens the ReceiptPaymentsDialog and keeps $ctrl.hasReceiptPayments in sync
 * with whatever it finds, so the "Receipt Payments" button (under the
 * checks/cards grid) can hide itself once nothing is left active. This is that
 * button's click handler - the dialog never opens on its own.
 *
 * However the dialog closes, the payments it looked up fresh are compared
 * against the ones this receipt was loaded with. If any were refunded or voided
 * elsewhere in the meantime (typically on the tab the dialog's own "go to
 * receipt" button opened), the loaded item balances - and so Balance Reduction -
 * are stale, so the receipt is reloaded. Otherwise nothing on the screen is
 * touched, so the user's selections are kept.
 *******************************************************************************/
/* @ngInject */ function showReceiptPayments(
  $ctrl,
  $routeParams,
  Alert,
  ReceiptPaymentsDialog
) {
  return function () {
    return ReceiptPaymentsDialog.show(Number($routeParams.id)).then(
      function (payments) {
        $ctrl.hasReceiptPayments = payments.length > 0;

        if (
          !ReceiptPaymentsDialog.paymentsChanged(
            $ctrl.receiptPayments,
            payments
          )
        )
          return;

        return Alert.show(
          'Payments against this receipt have changed on another receipt. ' +
            'The receipt will be reloaded with the updated balances.'
        ).then(function () {
          /*----------------------------------------------------*/
          /* The reload replaces the checks/cards grid data,    */
          /* which clears its selection without firing the      */
          /* selection-changed handlers - drop the old picks so */
          /* they don't linger in the return totals.            */
          /*----------------------------------------------------*/
          $ctrl.checksToReturn = [];
          $ctrl.cardsToReturn = [];

          return $ctrl.getReceipt();
        });
      }
    );
  };
}

/*****************************************************************************
 * returnComplete */
/**
 * After saving, or when canceling, check for any changes from the Watcher, and
 * change the route to the return route from the binding.
 *****************************************************************************/
/* @ngInject */ function returnComplete($ctrl, $global, $location, Alert) {
  return function () {
    Alert.clear();
    $global.Watch.checkForChanges(function (result) {
      /*-----------------------------------*/
      /* Reset back to the previous route. */
      /*-----------------------------------*/
      $location.path($ctrl.returnRoute);
    });
  };
}

/*****************************************************************************/
/* @ngInjest */ function savePreCheck($ctrl, Alert) {
  return function () {
    if ($ctrl.amounts.journal) {
      Alert.confirm(
        `You have setup an Offline Card Refund for this Return. It should
        only be used to record that a refund was done through your processor,
        and will require you to navigate to your processor's portal and perform the actual refund.
        Or contact support if you are unable to process a refund in the payment provider's portal.
        Are you sure you want to continue?
      `
      ).then(() => {
        $ctrl.saveReturn();
      });

      return;
    }

    $ctrl.saveReturn();
  };
}

/*****************************************************************************
 * saveReturn */
/**
 * Creates and saves the return.
 *****************************************************************************/
/* @ngInject */ function saveReturn(
  $ctrl,
  $global,
  Alert,
  BusyScreen,
  Data,
  ReturnsService,
  SERVER,
  Session,
  Tools
) {
  /*--------------------------------*/
  /* Create the save error handler. */
  /*--------------------------------*/
  var errFunction = function (err) {
    var message;
    var title = 'Return Receipts';
    if (angular.isObject(err)) message = err.parsedResponse.message();
    else {
      message = err.split('|')[0];
      title = err.split('|')[1];
    }

    BusyScreen.setBusy(false);
    Alert.error(message, title);
  };

  return function () {
    /*--------------------*/
    /* Validate the data. */
    /*--------------------*/
    $ctrl.validators.reset();
    if (!$ctrl.validators.validate()) return;

    if ($ctrl.amounts.refund <= 0) {
      Alert.error('Items must be selected for return', 'No Items Selected');
      return;
    }

    var refundPaidTotal =
      $ctrl.amounts.funds +
      $ctrl.amounts.cash +
      $ctrl.amounts.journal +
      $ctrl.amounts.check;

    if (refundPaidTotal < 0) {
      Alert.error(
        'The total that we are returning to the client is negative. Please adjust your amounts.',
        'Negative Total'
      );
      return;
    }

    /*-------------------------------------------------*/
    /* Check that the full refund amount has been paid */
    /*-------------------------------------------------*/
    if (refundPaidTotal < $ctrl.amounts.totalDue) {
      Alert.error(
        'The total amount due has not been fully refunded.',
        'Refund Owed'
      );
      return;
    }

    /*-----------------------------------------------------------*/
    /* Prevent the saving if more has overpaid than what is due. */
    /*-----------------------------------------------------------*/
    if (refundPaidTotal > $ctrl.amounts.totalDue) {
      Alert.error(
        'You are trying to give out more than what is owed. Please adjust your amounts.',
        'Excess Selected'
      );
      return;
    }

    /*-------------------------------------------*/
    /* Loop through the list of items and        */
    /* obtain the appropriate item return items. */
    /*-------------------------------------------*/
    var items = $ctrl.itemsGrid.data
      .filter((rigi) => rigi.hasReturn)
      .map(function (rigi) {
        /*---------------------------------------------------------*/
        /* Make a copy so we can set the quantity to a negative    */
        /* value in order to reverse the inventory sale total. For */
        /* the convenience fee, we can directly reverse its total. */
        /*---------------------------------------------------------*/
        rigi = Object.assign(
          Object.create(Data.ReturnItemGridItem.prototype),
          rigi
        );

        if (rigi.isAmountItem) {
          rigi.returnAmount = -rigi.returnAmount;
        } else {
          rigi.newQty = -rigi.returnQuantity;
        }

        /*------------------------------------------------------------*/
        /* Add the receivable credit if there is an amount to reduce. */
        /*------------------------------------------------------------*/
        if (rigi.balanceDue > 0) {
          rigi.receivable = rigi.getCreditMemo();
          rigi.receivable.entityID = $ctrl.receipt.entityID;
        }

        rigi.discountGASBCode = rigi.source.discountGASBCode;

        return rigi;
      });

    /*----------------------------------------------------------*/
    /* If there is a convenience fee being returned, create the */
    /* ledgerItem for it here, and add it to the list of items. */
    /*----------------------------------------------------------*/
    if ($ctrl.amounts.convenienceFee > 0) {
      const convenienceFee = $ctrl.receipt.receiptItems.find(
        (item) => item.type === SERVER.BooksDBConstants.LIT_SERVICE_FEE_CREDIT
      );

      if (convenienceFee) {
        items.push({
          accountID: convenienceFee.accountID,
          amount: -$ctrl.amounts.convenienceFee,
          gasbCode: convenienceFee.gasbCode,
          referenceItemID: convenienceFee.id,
          type: convenienceFee.type
        });
      }
    }

    /*-----------------------------------------------*/
    /* Convert the selected received checks to money */
    /* paid items with references to the original    */
    /* checks received for those that were selected. */
    /*-----------------------------------------------*/
    var payments = [];
    $ctrl.checksToReturn.forEach(function (checkItem) {
      payments.push({
        type: SERVER.BooksDBConstants.LIT_MONEY_PAID,
        subType: SERVER.BooksDBConstants.LIS_CHECK_RETURNED,
        name: checkItem.name,
        number: checkItem.number,
        amount: checkItem.amount,
        accountID: checkItem.accountID,
        referenceItemID: checkItem.id
      });
    });

    /*------------------------------------------------------------------*/
    /* If there are multiple cards being returned, we need to account   */
    /* for the possibility that they are returning less value in items  */
    /* than the selected card total. This will average the refund total */
    /* across all selected cards and set that for each card payment.    */
    /*------------------------------------------------------------------*/
    var cardRefundTotal = Math.min(
      $ctrl.amounts.refund,
      $ctrl.cardsToReturn.reduce(
        (total, card) => total + (card.amount - card.returnedAmount),
        0
      )
    );

    /*--------------------------------------------------------*/
    /* We are using Math.trunc here instead of                */
    /* Tools.roundMoney because we don't want it to round up. */
    /*--------------------------------------------------------*/
    var refundPerCard =
      Math.trunc((cardRefundTotal / $ctrl.cardsToReturn.length) * 100) / 100;

    $ctrl.cardsToReturn.forEach(function (cardItem) {
      payments.push({
        type: SERVER.BooksDBConstants.LIT_MONEY_PAID,
        subType: cardItem.subType,
        processorType: cardItem.processorType,
        name: cardItem.name,
        number: cardItem.number,
        amount: refundPerCard,
        accountID: cardItem.accountID,
        referenceItemID: cardItem.id
      });
    });

    /*---------------------------------------------*/
    /* If there is any remainder from the average, */
    /* it will be added to the last payment.       */
    /*---------------------------------------------*/
    var actualcardRefundTotal = refundPerCard * $ctrl.cardsToReturn.length;
    if (cardRefundTotal - actualcardRefundTotal > 0)
      payments[payments.length - 1].amount +=
        cardRefundTotal - actualcardRefundTotal;

    /*----------------------------*/
    /* Create the receipt refund. */
    /*----------------------------*/
    if ($ctrl.reductionToReceipt > 0) {
      payments.push({
        type: SERVER.BooksDBConstants.LIT_RECEIVABLE_CREDIT,
        subType: SERVER.BooksDBConstants.LIS_REFUND,
        referenceID: $ctrl.receivableCharge.referenceID,
        entityID: $ctrl.receivableCharge.entityID,
        name: $ctrl.receivableCharge.name,
        accountID: $ctrl.receivableCharge.accountID,
        memo: 'Reduction to Receipt #{0}'.format($ctrl.receipt.number),
        amount: $ctrl.reductionToReceipt
      });
    }

    /*-----------------------------*/
    /* Increase the student funds. */
    /*-----------------------------*/
    if ($ctrl.amounts.funds) {
      /*-----------------------------------------------*/
      /* Ensure that the Student Funds account is set. */
      /*-----------------------------------------------*/
      if (!$ctrl.studentFundsAccountID) {
        Alert.error(
          'You need to set your "Student Funds Account" in Account Admin to return any funds with a check.',
          'Account Not Set'
        );
        return;
      }

      payments.push({
        type: SERVER.BooksDBConstants.LIT_STUDENT_FUNDS_CREDIT,
        entityID: $ctrl.fundsEntity.id,
        accountID: $ctrl.studentFundsAccountID,
        amount: $ctrl.amounts.funds
      });
    }

    /*--------------------------------------------------------*/
    /* Create a cash returned item if there is a cash amount. */
    /*--------------------------------------------------------*/
    if ($ctrl.amounts.cash) {
      payments.push({
        type: SERVER.BooksDBConstants.LIT_MONEY_PAID,
        subType: SERVER.BooksDBConstants.LIS_CASH_RETURNED,
        accountID: $ctrl.mUFAccountID,
        amount: $ctrl.amounts.cash
      });
    }

    /*-------------------------------------------------------------*/
    /* For both Card return payment, and recorded payment, we need */
    /* to get the card payment(s) from the original receipt. If we */
    /* are just recording the payment, we only need the account    */
    /*-------------------------------------------------------------*/
    var cardPayments = $ctrl.receipt.paymentItems.filter(function (item) {
      return (
        item.subType === SERVER.BooksDBConstants.LIS_CREDIT_CARD ||
        item.subType === SERVER.BooksDBConstants.LIS_DEBIT_CARD ||
        item.subType === SERVER.BooksDBConstants.LIS_WEBSTORE ||
        item.subType === SERVER.BooksDBConstants.LIS_EXT_WEBSTORE
      );
    });

    var cardPayment = cardPayments.length > 0 ? cardPayments[0] : {};

    /*------------------------------------------------------------*/
    /* Create a journal return item if there is a journal amount. */
    /*------------------------------------------------------------*/
    if ($ctrl.amounts.journal) {
      payments.push({
        type: SERVER.BooksDBConstants.LIT_JOURNAL_CREDIT,
        subType: SERVER.BooksDBConstants.LIS_OFFLINE_RETURN,
        accountID: cardPayment.accountID,
        amount: $ctrl.amounts.journal,
        memo: 'Offline Return Card Record'
      });
    }

    /*-------------------------*/
    /* Create the return entry */
    /*-------------------------*/
    var returnReceipt = Data.ReturnReceipt($ctrl.receipt, $ctrl.returnDate);
    returnReceipt.receiptItems = items;
    returnReceipt.paymentItems = payments;
    returnReceipt.amount = -$ctrl.amounts.refund;
    var entity = $ctrl.entities.find(function (entity, i, a) {
      return entity.id === returnReceipt.entityID;
    });
    /**
     * If the entity does not exist we default to the receiptFrom because the CWS processed receipts will not
     * be attached to an entity if the CWS user that made the purchase is not a student. When a CWS is not a student
     * they do not need to exist in any of client DBs they just exist in CWS db.
     */
    returnReceipt.receiptFrom = entity
      ? entity.name
      : $ctrl.receipt.receiptFrom;

    /*-------------------------------------------*/
    /* Creates the check object if necessary,    */
    /* then continues to the final save routine. */
    /*-------------------------------------------*/
    $ctrl.buildCheck(payments).then(function (check) {
      if (check) returnReceipt.check = check;

      BusyScreen.setBusy(true);
      ReturnsService.save(returnReceipt).then(function (resp) {
        /*--------------------------------------------------*/
        /* Print the PDF and display printing instructions. */
        /*--------------------------------------------------*/
        window.open(
          Session.getSessionURL('/PrintReceiptPDF.aspx?receiptID=' + resp.id),
          '_blank'
        );
        BusyScreen.setBusy(false);
        $global.Watch.deregister();

        Alert.info(
          'This receipt has been created in a separate window as a PDF.\n\n' +
            'Click Ok when Finished Printing',
          'Print Receipt',
          true
        ).finally($ctrl.returnComplete);
      }, errFunction);
    }, errFunction);
  };
}

/*****************************************************************************
 * selectItem */
/**
 * Handles the item selection from the search button. Increases the return
 * quantity for the first matching item that hasn't been fully returned.
 *
 * @param  $item  The selected item to increase the quantity for.
 *****************************************************************************/
/* @ngInject */ function selectItem($ctrl) {
  return function ($item) {
    var itemID = $item.id;

    /*----------------------------------------------------------*/
    /* Find the first matching item, and increase its quantity, */
    /* or set it to the max quantity if we attempt to go over.  */
    /*----------------------------------------------------------*/
    $ctrl.itemsGrid.data.some(function (item) {
      if (item.itemID === itemID && !item.isFullReturn) {
        if (item.returnQuantity >= item.quantity)
          item.returnQuantity = item.quantity;
        else item.returnQuantity++;

        return true;
      }
    });
  };
}
